import { useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import CruiseLuxPlusInclusionList from './CruiseLuxPlusInclusionList'

import { buildAllStandardInclusions } from 'lib/cruises/cruiseInclusionsUtils'
import styled from 'styled-components'
import { rem } from 'polished'
import LabeledSheet from 'LuxKit/LabeledSheet'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LoyaltyInclusions from 'loyalty/components/LoyaltyInclusions'
import { hasBundleInclusions as getHasBundleInclusions } from 'lib/cruises/cruiseBundleFlightCredits'
import CruiseEvergreenInclusionList from './CruiseEvergreenInclusionList'
import CruiseBundleInclusions from './CruiseBundleInclusions'

const InclusionList = styled(VerticalSpacer)`
  padding: ${rem(8)};
`

interface Props {
  offer: App.CruiseOffer
  evergreenInclusions?: Array<App.PackageInclusion>
  tileInclusions?: Array<App.OfferInclusion>
  standardInclusions: Array<App.PackageInclusion>
  luxPlusInclusions: Array<App.PackageInclusion>
  gap?: React.ComponentProps<typeof Group>['gap']
}

function CruiseInclusionList({
  offer,
  standardInclusions,
  luxPlusInclusions,
  evergreenInclusions,
  tileInclusions,
  gap = 8,
}: Props) {
  const intl = useIntl()
  const allStandardInclusions = useMemo(
    () => buildAllStandardInclusions(standardInclusions, tileInclusions),
    [standardInclusions, tileInclusions],
  )

  const hasStandardInclusions = !!allStandardInclusions.length
  const hasBundleInclusions = getHasBundleInclusions(offer.bundle)
  const hasInclusions = hasStandardInclusions || !!luxPlusInclusions?.length || !!evergreenInclusions?.length || hasBundleInclusions

  return <>
    {hasInclusions && <Group direction="vertical" gap={gap} horizontalAlign="start">
      <CruiseLuxPlusInclusionList luxPlusInclusions={luxPlusInclusions} offer={offer} />

      {hasBundleInclusions && offer.bundle && <CruiseBundleInclusions
        bundle={offer.bundle}
        standardInclusions={allStandardInclusions}
      />}

      {!hasBundleInclusions && hasStandardInclusions && <LabeledSheet
        kind="highlight"
        label={<PromotionLabel icon={<SolidFireIcon />} variant="premium">
          {intl.formatMessage({
            id: '0gNTLTDxVcCH7_pXXfH6',
            defaultMessage: 'Special offer',
            description: 'Label for special offer on cruise inclusions in the cruise section',
          })}
        </PromotionLabel>}
      >
        <InclusionList gap={4}>
          {allStandardInclusions.map((inclusion) => <OfferInclusionItem
            key={inclusion.id}
            inclusion={inclusion}
            colour="highlight-primary-dark"
            isHighlighted
            showMinStayDescription
          />)}
        </InclusionList>
      </LabeledSheet>}
      <LoyaltyInclusions offer={offer} variant="offer-page" />

      {!hasBundleInclusions && !!evergreenInclusions?.length && <CruiseEvergreenInclusionList
        evergreenInclusions={evergreenInclusions}
        visibleItems={5}
      />}
    </Group>}
  </>
}

export default CruiseInclusionList
